/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AssessmentService } from 'app/core/services/assessment-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ApiModelIdentifier } from 'app/shared/models/essette-api';
import { apiCall } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class QuestionResolver {

    private questionTypes = new Map<ApiModelIdentifier, Core.AssessmentQuestionType>();
    public isInitialized = false;
    private _initializing: boolean = false;

    public constructor(
        private readonly env: EnvironmentService,
        private readonly service: AssessmentService,
        private readonly logger: AppLogger) {

        this.logger.source = this;
    }

    /**
     * Initializes resolver with Question Type data. If types are already available they may be passed in to avoid API call
     * @param types
     */
    public async init(types?: Core.AssessmentQuestionType[]): Promise<void> {
        if(this.isInitialized || this._initializing) { return; }

        this._initializing = true;
        if(!types || types.length === 0) {
            types = await this.loadTypes();
        }

        types.forEach(r => {
            if(r.id) {
                this.questionTypes.set(r.id, r);
            }
        });
        this.isInitialized = true;
        this._initializing = false;
    }

    /**
     * Must be initialized first. Returns the path to the correct component for the question
     * @param question
     */
    public resolveQuestionViewModel(question: Core.AssessmentQuestion): string | null {
        if(!this.isInitialized) {
            throw new Error('QuestionResolver has not been initialized.');
        }

        const type = this.questionTypes.get(question.type && question.type.id || '');
        if(!type) {
            this.logger.debug('Unable to resolve question component: type not found');
            return null;
        }

        question.type = type;

        return this.getQuestionUrl(type.code || '', question);
    }

    public resolveSharedQuestion(question: Core.AssessmentSharedQuestion): string | null {
        if(!this.isInitialized) {
            throw new Error('QuestionResolver has not been initialized.');
        }

        const type = this.questionTypes.get(question.type && question.type.id || '');
        if(!type) {
            this.logger.warn('Unable to resolve question component: type not found');
            return null;
        }

        question.type = type;

        return this.getQuestionUrl(type.code || '');
    }

    public resolveQuestionType(question: Core.AssessmentQuestion): string | null {
        const typeCode = question.type && question.type.code;
        if(typeCode !== 'ShareQuestion') { return typeCode || null; }

        if(question.sharedQuestion) {
            return this.resolveQuestionType(question.sharedQuestion) || null;
        }

        return null;
    }

    private getQuestionUrl(typeCode: string, question: Core.AssessmentQuestion | null = null): string | null {

        const view = this.env.getDynamicViewByKey('Core.AssessmentQuestionType', typeCode);
        if(view) {
            this.logger.debug(`Resolving question type code [${typeCode}] to '${view.title}' (${view.url}).`);
            return view.url;
        }

        switch(typeCode) {
            case 'SharedQuestion':
                if(question && question.sharedQuestion) {
                    this.logger.debug(`Resolving question type code [${typeCode}] to 'Shared Question'.`);
                    return this.resolveSharedQuestion(question.sharedQuestion);
                }
                break;
        }

        this.logger.warn(`Did not resolve question type [${typeCode}].`);
        return 'app/core/components/assessments/questions/question-textbox';
    }


    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadTypes(): Promise<Core.AssessmentQuestionType[]> {
        const fields: string[] = [
            'code'
        ];

        try {
            const response = await this.service.loadQuestionTypes({}, [], fields);
            return response.results;
        }
        catch(error) {
            this.logger.error('QuestionResolver.loadTypes(): Unable to load question types', error);
            //throw error;
        }
        return [];
    }

}
